import React, {
  createContext,
  useContext,
  useCallback,
  useReducer,
  useEffect,
  useRef,
} from "react";
import clsx from "clsx";

// ─── Types ───────────────────────────────────────────────────────────────────

export type OcToastVariant = "success" | "error" | "warning" | "info";

export interface OcToastItem {
  id: string;
  variant: OcToastVariant;
  title: string;
  description?: string;
  /** Milliseconds before auto-dismiss. Set to 0 to disable. Defaults: success=4000, others=0 (persistent). */
  duration?: number;
  /** Action button label for retry / undo. */
  actionLabel?: string;
  onAction?: () => void;
}

type OcToastAction =
  | { type: "ADD"; toast: OcToastItem }
  | { type: "REMOVE"; id: string };

// ─── Reducer ─────────────────────────────────────────────────────────────────

function ocToastReducer(state: OcToastItem[], action: OcToastAction): OcToastItem[] {
  switch (action.type) {
    case "ADD":
      return [action.toast, ...state].slice(0, 5); // max 5 visible
    case "REMOVE":
      return state.filter((t) => t.id !== action.id);
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface OcToastContextValue {
  toasts: OcToastItem[];
  dismiss: (id: string) => void;
  toast: (item: Omit<OcToastItem, "id">) => string;
}

const OcToastContext = createContext<OcToastContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

export function OcToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, dispatch] = useReducer(ocToastReducer, []);
  const counterRef = useRef(0);

  const dismiss = useCallback((id: string) => {
    dispatch({ type: "REMOVE", id });
  }, []);

  const toast = useCallback((item: Omit<OcToastItem, "id">): string => {
    const id = `oc-toast-${++counterRef.current}`;
    dispatch({ type: "ADD", toast: { ...item, id } });
    return id;
  }, []);

  return (
    <OcToastContext.Provider value={{ toasts, dismiss, toast }}>
      {children}
      <OcToastRegion toasts={toasts} onDismiss={dismiss} />
    </OcToastContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Returns convenience methods for showing toast notifications.
 *
 * - `success`: auto-dismisses after 4 s
 * - `error`: persistent until manually dismissed (includes retry slot)
 * - `warning` / `info`: persistent by default
 */
export function useOcToast() {
  const ctx = useContext(OcToastContext);
  if (!ctx) throw new Error("useOcToast must be used within OcToastProvider");

  const { toast, dismiss } = ctx;

  return {
    success: (title: string, description?: string) =>
      toast({ variant: "success", title, ...(description ? { description } : {}), duration: 4000 }),
    error: (title: string, description?: string, onAction?: () => void) =>
      toast({
        variant: "error",
        title,
        ...(description ? { description } : {}),
        duration: 0,
        ...(onAction ? { actionLabel: "Retry", onAction } : {}),
      }),
    warning: (title: string, description?: string) =>
      toast({ variant: "warning", title, ...(description ? { description } : {}), duration: 0 }),
    info: (title: string, description?: string) =>
      toast({ variant: "info", title, ...(description ? { description } : {}), duration: 6000 }),
    dismiss,
  };
}

// ─── Region (renderer) ───────────────────────────────────────────────────────

const OC_TOAST_STYLES: Record<OcToastVariant, { wrapper: string; dot: string }> = {
  success: {
    wrapper: "border-green-500/30 bg-white dark:bg-oc-navy-800",
    dot: "bg-green-400",
  },
  error: {
    wrapper: "border-red-500/30 bg-white dark:bg-oc-navy-800",
    dot: "bg-red-400",
  },
  warning: {
    wrapper: "border-amber-500/30 bg-white dark:bg-oc-navy-800",
    dot: "bg-amber-400",
  },
  info: {
    wrapper: "border-cyan-500/30 bg-white dark:bg-oc-navy-800",
    dot: "bg-cyan-400",
  },
};

function OcToastTile({
  toast,
  onDismiss,
}: {
  toast: OcToastItem;
  onDismiss: (id: string) => void;
}) {
  const styles = OC_TOAST_STYLES[toast.variant];

  useEffect(() => {
    if (!toast.duration) return;
    const timer = setTimeout(() => onDismiss(toast.id), toast.duration);
    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      className={clsx(
        "flex items-start gap-3 rounded-lg border px-4 py-3 shadow-lg shadow-black/40",
        "w-80 max-w-full text-sm text-slate-800 dark:text-slate-200 pointer-events-auto",
        styles.wrapper
      )}
    >
      <span
        className={clsx("mt-1.5 h-2 w-2 flex-shrink-0 rounded-full", styles.dot)}
        aria-hidden="true"
      />
      <div className="flex-1 min-w-0">
        <p className="font-medium leading-snug">{toast.title}</p>
        {toast.description && (
          <p className="mt-0.5 text-xs text-slate-400 leading-relaxed">{toast.description}</p>
        )}
        {toast.actionLabel && toast.onAction && (
          <button
            type="button"
            onClick={() => { toast.onAction?.(); onDismiss(toast.id); }}
            className="mt-1.5 text-xs font-semibold text-oc-teal-500 hover:text-oc-teal-400 underline-offset-2 hover:underline"
          >
            {toast.actionLabel}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
        className="flex-shrink-0 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors mt-0.5"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className="h-3.5 w-3.5"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// ─── Simple inline OcToast (for page-level state-driven toasts) ──────────────

export interface OcToastProps {
  message: string;
  type: "success" | "error" | "warning" | "info";
  onDismiss: () => void;
}

export function OcToast({ message, type, onDismiss }: OcToastProps) {
  const styles = OC_TOAST_STYLES[type];
  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex items-center gap-3 rounded-lg border px-4 py-3 shadow-lg shadow-black/40 text-sm text-slate-800 dark:text-slate-200 ${styles.wrapper}`}
    >
      <span className={`h-2 w-2 flex-shrink-0 rounded-full ${styles.dot}`} aria-hidden="true" />
      <span className="flex-1">{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-3.5 w-3.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

function OcToastRegion({
  toasts,
  onDismiss,
}: {
  toasts: OcToastItem[];
  onDismiss: (id: string) => void;
}) {
  if (toasts.length === 0) return null;

  return (
    <div
      aria-label="Notifications"
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none"
    >
      {toasts.map((t) => (
        <OcToastTile key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
